import { Link } from "react-router-dom";
import { routePage } from "../../lib/routePages";

const POPULAR = ["retiro-a-santiago-del-estero", "retiro-a-tucuman", "liniers-a-santiago-del-estero", "moreno-a-santiago-del-estero", "retiro-a-frias", "retiro-a-nueva-esperanza", "retiro-a-monte-quemado", "santiago-del-estero-a-retiro"];
import { ArrowUpRight } from "lucide-react";
import { WhatsAppIcon, InstagramIcon, FacebookIcon } from "./Icons";
import { PuntoEnvioMark } from "./PuntoEnvioMark";
import { NAV, WHATSAPP_URL, WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, PUNTOENVIO, ADDRESS, PHONE } from "../../lib/site";
import { COMPANY, GOV_LINKS, LEGAL_LINKS } from "../../lib/legal";
import { PaymentLogos } from "./PaymentLogos";
import { InstallApp } from "./InstallApp";
import { PushOptIn } from "./PushOptIn";

const LegalStrip = () => (
  <div data-testid="footer-legal" className="flex flex-col gap-6 border-t border-white/10 py-8 md:flex-row md:items-center md:justify-between">
    <div className="space-y-4">
      <Link to="/arrepentimiento" data-testid="footer-withdrawal-button" className="inline-flex items-center rounded-full border-2 border-[var(--lime)] px-5 py-2.5 text-sm font-bold text-[var(--lime)] transition-colors hover:bg-[var(--lime)] hover:text-[var(--ink)]">Botón de arrepentimiento</Link>
      <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/80">
        {LEGAL_LINKS.slice(1).map((l) => <Link key={l.to} to={l.to} data-testid={l.testid} className="hover:text-[var(--lime)]">{l.label}</Link>)}
        {GOV_LINKS.map((l) => <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={l.testid} className="inline-flex items-center gap-1 hover:text-[var(--lime)]">{l.label} <ArrowUpRight className="h-3.5 w-3.5" /></a>)}
      </nav>
      <p className="text-xs text-white/60">{COMPANY.name} · CUIT {COMPANY.cuit} · {COMPANY.iva} · {COMPANY.address}</p>
      <PaymentLogos testid="footer-payment-logos" />
    </div>
    <a href={COMPANY.dataFiscalUrl} target="_F960AFIPInfo" rel="noopener noreferrer" data-testid="footer-data-fiscal" className="flex shrink-0 items-center gap-3 self-start rounded-2xl bg-white p-2 pr-4 text-xs font-semibold text-[var(--ink)] md:self-auto">
      <img src="/legal/data-fiscal.jpg" alt="Data Fiscal ARCA" width="72" height="72" className="h-[72px] w-[72px]" />
      Data Fiscal<br />ARCA
    </a>
  </div>
);

const SOCIALS = [
  { href: INSTAGRAM_URL, label: "Instagram", Icon: InstagramIcon },
  { href: FACEBOOK_URL, label: "Facebook", Icon: FacebookIcon },
  { href: WHATSAPP_URL, label: "WhatsApp", Icon: WhatsAppIcon },
].filter((s) => s.href);

const Col = ({ title, children }) => (
  <div>
    <p className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-[var(--lime)]">{title}</p>
    <div className="space-y-3 text-[15px] text-white/80">{children}</div>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="grain relative overflow-hidden bg-[var(--night)] text-white">
    <div className="mx-auto max-w-[1400px] px-5 pb-10 pt-20 md:px-10">
      <div className="flex flex-col gap-10 border-b border-white/10 pb-14 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-display text-4xl leading-[0.95] sm:text-5xl lg:text-6xl">¿Hablamos<br />por WhatsApp?</p>
          <p className="mt-4 max-w-md text-white/75">Pasajes, horarios y encomiendas. Te respondemos rápido.</p>
        </div>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-button" className="group inline-flex items-center gap-3 self-start rounded-full bg-[#25D366] px-7 py-4 text-lg font-bold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-1 lg:self-auto">
          <WhatsAppIcon className="h-6 w-6" /> Escribinos ahora
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
        </a>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-3"><InstallApp testid="footer-install-app" /><PushOptIn testid="footer-push" dark /></div>

      <div className="grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src="/brand/logo-white.png" alt="Santiagueño" className="h-12 w-auto md:h-14" />
          <p className="mt-5 max-w-xs text-white/75">Conectamos Buenos Aires, Rosario y Santa Fe con Santiago del Estero y Tucumán.</p>
          <div data-testid="footer-santiagueno-contact" className="mt-4 space-y-1 text-[15px] text-white/80">
            <p>{ADDRESS}</p>
            <p>Teléfono: <a href={`tel:${PHONE}`} className="hover:text-[var(--lime)]">{PHONE}</a></p>
            <p>WhatsApp: <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--lime)]">{WHATSAPP_NUMBER}</a></p>
          </div>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ href, label, Icon }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${label.toLowerCase()}`} className="grid h-12 w-12 place-items-center rounded-full border border-white/20 transition-[background-color,color,border-color] duration-300 hover:border-[var(--lime)] hover:bg-[var(--lime)] hover:text-[var(--ink)]">
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
        <Col title="Navegación">
          {NAV.map((n) => <Link key={n.to} to={n.to} data-testid={`footer-nav-${n.label.toLowerCase()}`} className="block transition-colors hover:text-[var(--lime)]">{n.label}</Link>)}
        </Col>
        <Col title="Pasajes más buscados">
          {POPULAR.map((s) => routePage(s) && <Link key={s} to={`/pasajes/${s}`} data-testid={`footer-route-${s}`} className="block transition-colors hover:text-[var(--lime)]">{routePage(s).origin} → {routePage(s).destination}</Link>)}
        </Col>
        <Col title="Encomiendas">
          <PuntoEnvioMark size="text-3xl md:text-4xl" />
          <p>{PUNTOENVIO.address}</p>
          <p>Teléfono: <a href={`tel:${PUNTOENVIO.phone}`} data-testid="footer-puntoenvio-phone" className="hover:text-[var(--lime)]">{PUNTOENVIO.phone}</a></p>
          <p>WhatsApp: <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--lime)]">{PUNTOENVIO.whatsapp}</a></p>
          <a href={PUNTOENVIO.url} target="_blank" rel="noopener noreferrer" data-testid="footer-puntoenvio-link" className="inline-flex items-center gap-1 hover:text-[var(--lime)]">puntoenvio.com <ArrowUpRight className="h-4 w-4" /></a>
        </Col>
      </div>

      <LegalStrip />
      <div className="flex flex-col gap-3 border-t border-white/10 pt-8 text-sm text-white/60 md:flex-row md:justify-between">
        <p>© {new Date().getFullYear()} Santiagueño · <span data-testid="footer-company">{COMPANY.name} · CUIT {COMPANY.cuit}</span>. Todos los derechos reservados.</p>
        <p>Horarios y tarifas orientativos, sujetos a cambios. <Link to="/admin" data-testid="footer-admin-link" className="ml-2 underline-offset-4 hover:underline">Acceso privado</Link></p>
      </div>
    </div>
    <p aria-hidden="true" className="pointer-events-none select-none whitespace-nowrap px-4 pb-4 text-center font-display text-[17vw] leading-[0.8] text-white/[0.04]">SANTIAGUEÑO</p>
  </footer>
);
